/**
 * /notifications — Full inbox with filter chips + pagination.
 *
 * Filters: All · Unread · Applications · Coach
 * Loads 20 items at a time, "Load more" appends until `items.length >= total`.
 * Clicking a row marks it read and follows the deeplink.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, Bell } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

const PAGE = 20;

const FILTERS = [
  { id: "all",          label: "All" },
  { id: "unread",       label: "Unread" },
  { id: "applications", label: "Applications" },
  { id: "coach",        label: "Coach" },
];

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - then) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function NotificationsInbox() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyMore, setBusyMore] = useState(false);

  const fetchPage = useCallback(
    async (nextFilter, skip = 0, append = false) => {
      if (append) setBusyMore(true);
      else setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("limit", String(PAGE));
        params.set("skip", String(skip));
        if (nextFilter && nextFilter !== "all") params.set("filter", nextFilter);
        const { data } = await apiClient.get(`/notifications/inbox?${params.toString()}`);
        const rows = Array.isArray(data) ? data : (data?.items || []);
        const t = Array.isArray(data) ? rows.length : (data?.total ?? rows.length);
        setItems((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(t);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load notifications");
      } finally {
        setLoading(false);
        setBusyMore(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchPage(filter, 0, false);
  }, [filter, fetchPage]);

  const openItem = async (it) => {
    if (!it.read) {
      apiClient.post(`/notifications/inbox/${it.notification_id}/read`).catch(() => {});
      setItems((rows) => rows.map((r) => (r.notification_id === it.notification_id ? { ...r, read: true } : r)));
    }
    if (it.deeplink) navigate(it.deeplink);
  };

  const markAll = async () => {
    try {
      await apiClient.post("/notifications/mark-all-read");
      setItems((rows) => rows.map((r) => ({ ...r, read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Could not mark all as read");
    }
  };

  const canLoadMore = items.length < total;

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1", paddingBottom: 40 }} data-testid="notifications-inbox">
      {/* Header */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "#F4F3F1",
          borderBottom: "1px solid #E7E5E1",
        }}
      >
        <div className="max-w-2xl mx-auto flex items-center gap-3 px-4" style={{ height: 56 }}>
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="notif-back"
            className="w-9 h-9 grid place-items-center rounded-full"
            style={{ background: "#FFFFFF", border: "1px solid #E7E5E1" }}
          >
            <ChevronLeft size={18} color="#1A1A1A" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#1A1A1A", letterSpacing: "-0.01em", lineHeight: 1 }}>
              Notifications
            </h1>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#8A8884", marginTop: 2 }}>
              {total} total
            </div>
          </div>
          <button
            onClick={markAll}
            data-testid="notif-mark-all-page"
            style={{
              padding: "8px 14px",
              borderRadius: 100,
              background: "transparent",
              border: "1px solid #E7E5E1",
              color: "#1A1A1A",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Mark all read
          </button>
        </div>

        {/* Filter pills */}
        <div className="max-w-2xl mx-auto px-4 pb-3" style={{ display: "flex", gap: 8, overflowX: "auto" }}>
          {FILTERS.map((f) => {
            const on = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`notif-filter-${f.id}`}
                style={{
                  flexShrink: 0,
                  padding: "7px 14px",
                  borderRadius: 100,
                  background: on ? "#1A1A1A" : "#FFFFFF",
                  color: on ? "#F4F3F1" : "#1A1A1A",
                  border: on ? "1px solid #1A1A1A" : "1px solid #E7E5E1",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-2xl mx-auto" style={{ padding: "16px" }}>
        {loading && (
          <div style={{ padding: 40, textAlign: "center", color: "#8A8884", fontSize: 13 }}>Loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#FFFFFF", border: "1px solid #E7E5E1", display: "grid", placeItems: "center", margin: "0 auto" }}>
              <Bell size={26} color="#B8B4AE" />
            </div>
            <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A", marginTop: 16 }}>
              You&apos;re all caught up
            </div>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#8A8884", marginTop: 4 }}>
              {filter === "unread" ? "No unread notifications right now." : "Nothing in this filter yet."}
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="notif-list">
            {items.map((it) => (
              <button
                key={it.notification_id}
                onClick={() => openItem(it)}
                data-testid={`notif-row-${it.notification_id}`}
                style={{
                  textAlign: "left",
                  display: "flex",
                  gap: 12,
                  padding: "14px 14px",
                  background: it.read ? "#FFFFFF" : "rgba(0,212,255,0.08)",
                  border: `1px solid ${it.read ? "#EEECE9" : "rgba(0,212,255,0.3)"}`,
                  borderRadius: 14,
                  cursor: it.deeplink ? "pointer" : "default",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    marginTop: 6,
                    borderRadius: "50%",
                    background: it.read ? "transparent" : "#00D4FF",
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: 17,
                      color: "#1A1A1A",
                      lineHeight: 1.2,
                    }}
                  >
                    {it.title}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 13,
                      color: "#4A4845",
                      marginTop: 4,
                      lineHeight: 1.5,
                    }}
                  >
                    {it.message}
                  </div>
                  <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, color: "#8A8884" }}>
                      {fmtWhen(it.created_at)}
                    </span>
                    {it.type && (
                      <span
                        style={{
                          padding: "2px 7px",
                          borderRadius: 100,
                          background: "#F4F3F1",
                          border: "1px solid #E7E5E1",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 9,
                          color: "#6B6760",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        {(it.type || "").replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                </div>
                {it.read && (
                  <Check size={14} color="#B8B4AE" style={{ marginTop: 4, flexShrink: 0 }} />
                )}
              </button>
            ))}

            {canLoadMore && (
              <button
                onClick={() => fetchPage(filter, items.length, true)}
                disabled={busyMore}
                data-testid="notif-load-more"
                style={{
                  marginTop: 6,
                  height: 44,
                  borderRadius: 100,
                  background: "transparent",
                  border: "1px solid #E7E5E1",
                  color: "#1A1A1A",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: busyMore ? "not-allowed" : "pointer",
                }}
              >
                {busyMore ? "Loading…" : `Load more (${total - items.length} left)`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
